import { Trans, useLingui } from '@lingui/react/macro'
import { useRuntimeVariables } from 'app-kit'
import { Button } from 'designSystem/components/Button'
import { useGeckoAuth } from 'features/auth'
import {
  Badge,
  Callout,
  Hint,
  Panel,
  PanelTitle,
  Row,
} from 'features/sentinelDesign/designStyles'
import { FC, useCallback, useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import styled from 'styled-components'

interface CaseDiagnosis {
  content: string
  documents?: { document_id: string; title: string }[]
  created_at?: string
}

interface SentinelCase {
  case_id: string
  name: string
  summary?: {
    status?: string
    target?: string
    method?: string
    breach_ts?: string
    residual?: number
    threshold?: number
    magnitude?: number
    actual?: number | null
    expected?: number | null
  }
  diagnosis?: CaseDiagnosis | null
}

const CaseRow = styled.button`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  border-top: 0.0625rem solid ${({ theme }) => theme.colors.grayscale['700']};
  padding: 0.5rem 0;
  cursor: pointer;
  color: inherit;
  font: inherit;
`

const Detail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 0.4rem 0 0.8rem;
`

const Diagnosis = styled.div`
  font-size: 0.8125rem;
  line-height: 1.5;
  background: ${({ theme }) => theme.colors.grayscale['800']};
  border-radius: 0.375rem;
  padding: 0.75rem;

  /* The RCA agent returns markdown; render its structure. */
  p {
    margin: 0 0 0.5em;
  }
  p:last-child {
    margin-bottom: 0;
  }
  ul,
  ol {
    margin: 0.25em 0;
    padding-left: 1.25em;
  }
  li {
    margin: 0.15em 0;
  }
  strong {
    font-weight: 700;
  }
  code {
    font-family: 'Monaco', 'Menlo', monospace;
    font-size: 0.9em;
  }
  h1,
  h2,
  h3,
  h4 {
    font-size: 0.875rem;
    margin: 0.5em 0 0.25em;
  }
`

const num = (v: number | null | undefined, digits = 3) =>
  typeof v === 'number' && Number.isFinite(v) ? v.toPrecision(digits) : '—'

const when = (iso: string | undefined) =>
  iso ? new Date(iso).toLocaleString() : '—'

export interface CasesPanelProps {
  entryId: string
  site: string
  /** Bump to force a re-fetch (e.g. after a case is opened from the chart). */
  refreshKey?: number
  /**
   * A case to expand on open — from clicking its bubble on the chart. The
   * `nonce` re-fires expansion when the same case is clicked again. */
  focusCase?: { caseId: string; nonce: number } | null
}

/**
 * The cases opened for a sentinel: each is a point where the target left its
 * band. Clicking one reveals the breach detail and a button that runs the RCA
 * agent to diagnose the real-world cause (distinct from the model evaluation).
 */
export const CasesPanel: FC<CasesPanelProps> = ({
  entryId,
  site,
  refreshKey = 0,
  focusCase = null,
}) => {
  const { t } = useLingui()

  const { authorizedAxios } = useGeckoAuth()
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const base = `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(
    site
  )}/sentinel`

  const [cases, setCases] = useState<SentinelCase[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  const [diagnosing, setDiagnosing] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await authorizedAxios.get<{ cases: SentinelCase[] }>(
        `${base}/${encodeURIComponent(entryId)}/cases`
      )
      setCases(res.data.cases ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [authorizedAxios, base, entryId])

  useEffect(() => {
    // `load` is keyed on entryId; refreshKey is an explicit re-fetch trigger.
    void load()
  }, [load, refreshKey])

  // Expand the case whose bubble was clicked on the chart (nonce re-fires it).
  useEffect(() => {
    if (focusCase) setOpenId(focusCase.caseId)
  }, [focusCase])

  const runRca = async (caseId: string) => {
    setDiagnosing(caseId)
    setError(null)
    try {
      const res = await authorizedAxios.post<{ diagnosis: CaseDiagnosis }>(
        `${base}/case/${encodeURIComponent(caseId)}/diagnose`
      )
      setCases((prev) =>
        prev.map((c) =>
          c.case_id === caseId
            ? {
                ...c,
                diagnosis: res.data.diagnosis,
                summary: { ...c.summary, status: 'diagnosed' },
              }
            : c
        )
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setDiagnosing(null)
    }
  }

  return (
    <Panel aria-label={t`Cases`}>
      <Row>
        <PanelTitle>
          <Trans>Cases</Trans>
        </PanelTitle>
        <Badge>{cases.length}</Badge>
      </Row>
      <Hint>
        <Trans>
          Each case is a point where the target left its band. Open one and run
          the RCA agent to diagnose the real-world cause.
        </Trans>
      </Hint>

      {loading && (
        <Hint>
          <Trans>Loading cases…</Trans>
        </Hint>
      )}
      {error && <Callout $tone='error'>{error}</Callout>}
      {!loading && cases.length === 0 && (
        <Hint>
          <Trans>
            No cases yet — none of this sentinel&apos;s ticks breached.
          </Trans>
        </Hint>
      )}

      {cases.map((c) => {
        const s = c.summary ?? {}
        const open = openId === c.case_id
        return (
          <div key={c.case_id}>
            <CaseRow
              onClick={() => setOpenId(open ? null : c.case_id)}
              aria-expanded={open}>
              <span>{when(s.breach_ts)}</span>
              <Row>
                <Badge $tone='warning'>
                  {typeof s.magnitude === 'number'
                    ? t`${{ value234_27: s.magnitude.toFixed(1) }}x band`
                    : 'breach'}
                </Badge>
                <Badge>
                  {c.diagnosis ? 'diagnosed' : (s.status ?? 'open')}
                </Badge>
              </Row>
            </CaseRow>
            {open && (
              <Detail>
                <Hint>
                  <Trans>Target </Trans>
                  {s.target ?? '—'}
                  <Trans> · method </Trans>
                  {s.method ?? '—'}
                  <Trans> · measured</Trans> {num(s.actual)}
                  <Trans> vs expected </Trans>
                  {num(s.expected)}
                  <Trans> · residual</Trans> {num(s.residual)}
                  <Trans> (band ±</Trans>
                  {num(s.threshold)})
                </Hint>
                {c.diagnosis ? (
                  <>
                    <Diagnosis>
                      <ReactMarkdown>
                        {c.diagnosis.content || t`No diagnosis text.`}
                      </ReactMarkdown>
                    </Diagnosis>
                    {(c.diagnosis.documents?.length ?? 0) > 0 && (
                      <Hint>
                        <Trans>Referenced:</Trans>{' '}
                        {c.diagnosis.documents
                          ?.map((d) => d.title || d.document_id)
                          .join(', ')}
                      </Hint>
                    )}
                    <Row>
                      <Button
                        variant='secondary'
                        disabled={diagnosing === c.case_id}
                        onClick={() => void runRca(c.case_id)}>
                        {diagnosing === c.case_id ? t`Running…` : t`Re-run RCA`}
                      </Button>
                    </Row>
                  </>
                ) : (
                  <Row>
                    <Button
                      variant='primaryRaised'
                      disabled={diagnosing === c.case_id}
                      onClick={() => void runRca(c.case_id)}>
                      {diagnosing === c.case_id
                        ? t`Diagnosing… (this can take a minute)`
                        : t`Run RCA`}
                    </Button>
                  </Row>
                )}
              </Detail>
            )}
          </div>
        )
      })}
    </Panel>
  )
}
